import React from 'react';
import { useHistory, Link } from 'react-router-dom';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { Auth } from '@aws-amplify/auth';
import { ArrowLeftIcon, CheckCircle2 } from 'lucide-react';
import { LoadingMessageCard } from '@/components/LoadingMessageCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { FieldError } from '@/components/ui/field';
import { FormFields } from '@/components/Inputs/FormFields';
import { makeFormFieldCreator } from '@/components/Inputs/FieldCreator';
import { filterObjByArray } from '@/utils/filterInput';
import { useAuthActions, useAuthState } from '@/utils/auth';
import useLocalSetting from '@/hooks/useLocalSetting';
import {
  IdentityClaimKind,
  useCancelIdentityClaimMutation,
  useConfirmIdentityClaimMutation,
  useRequestIdentityClaimMutation,
} from '@/generated/codegen';
import {
  CONFIRM_IDENTITY_CLAIM_INPUT,
  REQUEST_IDENTITY_CLAIM_INPUT,
} from './IdentityClaimSchema';
import {
  OWN_PASSWORD_MESSAGE,
  OWN_PASSWORD_PATTERN,
  noEmailKey,
} from './identityClaimConstants';
import { IdentityClaimMergeStep } from './IdentityClaimMergeStep';
import { captureIdentityClaim } from '@/utils/analytics/identityClaimEvents';
import { GENERIC_ERROR_MESSAGE } from './identityClaimCognito';
import {
  CodeValues,
  codeFormFields,
  codeFormSchema,
  cognitoCodeErrorMessage,
  cognitoHasConfirmedEmail,
  sendEmailCode,
} from '@/utils/emailCode';

type Step = 'email' | 'code' | 'password' | 'merge' | 'done';

const STEP_NUMBER: Record<'email' | 'code' | 'password', number> = {
  email: 1,
  code: 2,
  password: 3,
};

const apiErrorMessage = (error: unknown): string =>
  error instanceof Error && error.message
    ? error.message
    : GENERIC_ERROR_MESSAGE;

const cognitoErrorMessage = (error: unknown): string =>
  cognitoCodeErrorMessage(error) ?? GENERIC_ERROR_MESSAGE;

/** First two characters of the local part + a fixed mask + the domain. */
const maskEmail = (email: string): string => {
  const [local, domain] = email.split('@');
  return `${(local ?? '').slice(0, 2)}••••@${domain ?? ''}`;
};

type EmailValues = { email: string };
const emailFieldCreator = makeFormFieldCreator<EmailValues>();
const emailFormFields = [
  emailFieldCreator({
    name: 'email',
    label: 'Email',
    inputProps: { type: 'email', autoComplete: 'email' },
  }),
];
const emailFormSchema = Yup.object().shape({
  email: Yup.string()
    .email('Ingresá un email válido')
    .required('Campo obligatorio'),
});

type PasswordValues = { password: string; password_confirm: string };
const passwordFieldCreator = makeFormFieldCreator<PasswordValues>();
const passwordFormFields = [
  passwordFieldCreator({
    name: 'password',
    label: 'Clave nueva',
    inputProps: { type: 'password', autoComplete: 'new-password' },
  }),
  passwordFieldCreator({
    name: 'password_confirm',
    label: 'Repetí la clave',
    inputProps: { type: 'password', autoComplete: 'new-password' },
  }),
];
const passwordFormSchema = Yup.object().shape({
  password: Yup.string()
    .matches(OWN_PASSWORD_PATTERN, OWN_PASSWORD_MESSAGE)
    .required('Campo obligatorio'),
  password_confirm: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Las contraseñas no coinciden.')
    .required('Campo obligatorio'),
});

const IdentityClaimPage: React.FC = () => {
  const history = useHistory();
  const { user } = useAuthState();
  const { signOut, refreshUser } = useAuthActions();
  const [, setNoEmail] = useLocalSetting<boolean>(noEmailKey(user?.id ?? 0));
  const claim = user?.identity_claim;

  const [step, setStep] = React.useState<Step>(
    claim?.kind === IdentityClaimKind.Merge ? 'merge' : 'email'
  );
  const [email, setEmail] = React.useState(claim?.requested_email ?? '');
  const [pendingEmail, setPendingEmail] = React.useState(
    claim?.requested_email ?? null
  );
  const [resuming, setResuming] = React.useState(
    claim?.kind === IdentityClaimKind.FreeEmail
  );
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [codeError, setCodeError] = React.useState<string | null>(null);
  const [passwordError, setPasswordError] = React.useState<string | null>(null);

  const [requestIdentityClaim] = useRequestIdentityClaimMutation();
  const [confirmIdentityClaim] = useConfirmIdentityClaimMutation();
  const [cancelIdentityClaim] = useCancelIdentityClaimMutation();

  const resumeEmail =
    claim?.kind === IdentityClaimKind.FreeEmail ? claim.requested_email : null;
  React.useEffect(() => {
    if (!resumeEmail) return;
    let cancelled = false;
    cognitoHasConfirmedEmail(resumeEmail).then((confirmed) => {
      if (cancelled) return;
      if (confirmed) setStep('password');
      setResuming(false);
    });
    return () => {
      cancelled = true;
    };
  }, [resumeEmail]);

  if (!user?.is_managed && step !== 'done') {
    return (
      <div className="flex flex-col gap-4 p-4">
        <p>Tu cuenta ya es propia.</p>
        <Link to="/" className="text-primary underline">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const goBack = () => {
    if (step === 'email') history.push('/');
    else if (step === 'password') setStep('code');
    else setStep('email');
  };

  const handleEmailSubmit = async (
    values: EmailValues,
    { setSubmitting }: FormikHelpers<EmailValues>
  ) => {
    setEmailError(null);
    captureIdentityClaim('identity_claim_email_submitted');
    try {
      const { data } = await requestIdentityClaim({
        variables: {
          input: filterObjByArray(REQUEST_IDENTITY_CLAIM_INPUT, values),
        },
      });
      const requested = data?.requestIdentityClaim;
      if (!requested) throw new Error(GENERIC_ERROR_MESSAGE);
      setEmail(requested.requested_email);
      setPendingEmail(requested.requested_email);
      refreshUser();
      if (requested.kind === IdentityClaimKind.Merge) {
        setStep('merge');
        return;
      }
      try {
        await sendEmailCode(requested.requested_email);
      } catch (error) {
        setEmailError(cognitoErrorMessage(error));
        return;
      }
      setCodeError(null);
      setStep('code');
    } catch (error) {
      captureIdentityClaim('identity_claim_email_rejected');
      setEmailError(apiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setEmailError(null);
    try {
      await cancelIdentityClaim();
      setPendingEmail(null);
      setEmail('');
      refreshUser();
      toast.success('Cancelaste el pedido.');
    } catch (error) {
      setEmailError(apiErrorMessage(error));
    }
  };

  const handleNoEmail = () => {
    captureIdentityClaim('identity_claim_no_email_clicked');
    setNoEmail(true);
    toast.success('Gracias por avisarnos.');
    history.push('/');
  };

  const handleCodeSubmit = async (
    values: CodeValues,
    { setSubmitting }: FormikHelpers<CodeValues>
  ) => {
    setCodeError(null);
    try {
      await Auth.verifyCurrentUserAttributeSubmit('email', values.code);
      captureIdentityClaim('identity_claim_code_confirmed');
      setPasswordError(null);
      setStep('password');
    } catch (error) {
      setCodeError(cognitoErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setCodeError(null);
    try {
      await sendEmailCode(email);
      toast.success('Te mandamos un código nuevo.');
    } catch (error) {
      setCodeError(cognitoErrorMessage(error));
    }
  };

  const handlePasswordSubmit = async (
    values: PasswordValues,
    { setSubmitting }: FormikHelpers<PasswordValues>
  ) => {
    setPasswordError(null);
    captureIdentityClaim('identity_claim_password_set');
    try {
      await confirmIdentityClaim({
        variables: {
          input: filterObjByArray(CONFIRM_IDENTITY_CLAIM_INPUT, values),
        },
      });
      captureIdentityClaim('identity_claim_completed');
      setStep('done');
    } catch (error) {
      setPasswordError(apiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      {step !== 'done' && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Volver"
              onClick={goBack}
            >
              <ArrowLeftIcon className="size-5" />
            </Button>
            <h1 className="text-lg font-bold">Hacé tuya tu cuenta</h1>
          </div>
          {step !== 'merge' && (
            <>
              <p className="text-sm text-muted-foreground">
                Paso {STEP_NUMBER[step]} de 3
              </p>
              <Progress value={(STEP_NUMBER[step] / 3) * 100} className="h-1" />
            </>
          )}
        </div>
      )}

      {resuming && <LoadingMessageCard message="Revisando tu pedido..." />}

      {!resuming && step === 'email' && (
        <Card>
          <CardHeader>
            <CardTitle>Tu email</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm">
              {`Esta cuenta es de ${user?.first_name} ${user?.last_name} (usuario ${user?.username}). Si no sos ${user?.first_name}, volvé atrás.`}
            </p>
            <Formik<EmailValues>
              initialValues={{ email }}
              validationSchema={emailFormSchema}
              onSubmit={handleEmailSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="flex flex-col gap-4">
                  <FormFields fields={emailFormFields} />
                  <p className="text-sm text-muted-foreground">
                    Puede ser el Gmail de tu celular. Te mandamos un código para
                    confirmar que es tuyo.
                  </p>
                  {pendingEmail && (
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span>{`Tenés un pedido para ${pendingEmail}.`}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleCancel}
                      >
                        Cancelar pedido
                      </Button>
                    </div>
                  )}
                  {emailError && <FieldError>{emailError}</FieldError>}
                  <Button type="submit" disabled={isSubmitting}>
                    Enviar código
                  </Button>
                  <Button type="button" variant="ghost" onClick={handleNoEmail}>
                    No tengo email
                  </Button>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      )}

      {!resuming && step === 'code' && (
        <Card>
          <CardHeader>
            <CardTitle>Revisá tu email</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm">
              {`Te mandamos un código de 6 números a ${maskEmail(email)}. Si no lo ves, fijate en Spam.`}
            </p>
            <Formik<CodeValues>
              initialValues={{ code: '' }}
              validationSchema={codeFormSchema}
              onSubmit={handleCodeSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="flex flex-col gap-4">
                  <FormFields fields={codeFormFields} />
                  {codeError && <FieldError>{codeError}</FieldError>}
                  <Button type="submit" disabled={isSubmitting}>
                    Confirmar
                  </Button>
                  <div className="flex justify-between">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleResendCode}
                    >
                      Reenviar código
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setStep('email')}
                    >
                      Cambiar email
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      )}

      {!resuming && step === 'password' && (
        <Card>
          <CardHeader>
            <CardTitle>Elegí tu clave</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm">
              Solo vos la vas a saber. Tu compañía deja de conocerla, y se
              cierra tu sesión en todos los celulares.
            </p>
            <Formik<PasswordValues>
              initialValues={{ password: '', password_confirm: '' }}
              validationSchema={passwordFormSchema}
              onSubmit={handlePasswordSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="flex flex-col gap-4">
                  <FormFields fields={passwordFormFields} />
                  {passwordError && <FieldError>{passwordError}</FieldError>}
                  <Button type="submit" disabled={isSubmitting}>
                    Guardar
                  </Button>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      )}

      {!resuming && step === 'merge' && (
        <IdentityClaimMergeStep requestedEmail={email} />
      )}

      {step === 'done' && (
        <Card>
          <CardHeader className="items-center text-center">
            <CheckCircle2 className="size-12 text-success" />
            <CardTitle>Tu cuenta es tuya</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-center">
              Desde ahora entrás con tu email o con tu usuario, y tu clave
              nueva.
            </p>
            <div className="flex flex-col gap-1 rounded-md border p-3 text-sm">
              <span>
                <strong>Email:</strong> {email}
              </span>
              <span>
                <strong>Usuario:</strong> {user?.username}
              </span>
            </div>
            <p className="text-sm text-center">
              Tus partes, tus máquinas y tus roles siguen igual.
            </p>
            <Button type="button" onClick={() => signOut()}>
              Entrar con tu clave nueva
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default IdentityClaimPage;
